/* The smaller composites of piece 4: the three async states, the tree, the
   permission grid, the drag-and-drop list, the file drop zone, the file
   viewer and the address checker. Every colour, size and gap is a token. */

/* Loading, empty and failed: three renderings, never one blank area. */
.asyncPanel {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-4);
  border-radius: var(--radius-medium);
  background: var(--surface-2);
  color: var(--text-muted);
}

.asyncPanel[data-async='failed'] {
  border-left: var(--tree-bar-width) solid var(--status-error-text);
}

.spinner {
  width: var(--icon-size-header);
  height: var(--icon-size-header);
  border-radius: var(--radius-stadium);
  border: var(--control-border-width) solid var(--border);
  border-top-color: var(--navy-primary);
  animation: spin 900ms linear infinite;
}

/*
 * 🔒 The refreshing mark (decision 1431): a small ring in the window's lower
 * corner, out of the flow, so a reload moves nothing. 💰 Placed against the
 * nearest region it sat under the banner or off the screen on a record page
 * (found while photographing, 2026-09-30).
 */
.refreshing {
  position: fixed;
  /* Clear of the footer bar (its medium button between two space-3 paddings, plus the phone's home indicator), so the ring never sits on Save or Send. */
  bottom: calc(var(--space-4) + var(--button-height-medium) + var(--space-3) * 2 + var(--safe-bottom));
  inset-inline-end: var(--space-4);
  z-index: 1;
  width: var(--icon-size-header);
  height: var(--icon-size-header);
  border-radius: var(--radius-stadium);
  border: var(--control-border-width) solid var(--border);
  border-top-color: var(--navy-primary);
  animation: spin 900ms linear infinite;
  pointer-events: none;
}

/* A reread that failed keeps the answer drawn below this line. */
.reloadFailed {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-2);
  border-left: var(--tree-bar-width) solid var(--status-error-text);
  background: var(--surface-2);
  color: var(--text-muted);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner,
  .refreshing {
    animation: none;
  }
}

/* The list reset (T3562): no bullet, no margin, no padding. A list names it beside its own class in the markup,
   `className={`${sharedStyles.listReset} ${styles.lines}`}`, and its own class adds only layout (decision 11321). */
.listReset {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* Nested Tree List. */
.tree {
  margin: 0;
  padding: 0;
  list-style: none;
}

.treeRow {
  padding-left: calc(var(--tree-depth) * var(--tree-indent));
}

.treeLine {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--list-viewer-row-height);
  padding-right: var(--space-2);
  border-bottom: 1px solid var(--surface-2);
}

.treeBar {
  flex: none;
  align-self: stretch;
  width: var(--tree-bar-width);
  border-radius: var(--radius-small);
  background: var(--tree-bar);
}

/* A filled row (decision 1290): the level colour across the row's full width,
   and every piece of text on it in the ink its contrast chose. */
.treeLine[data-fill] {
  background: var(--tree-bar);
  color: var(--tree-ink);
}

.treeLine[data-fill] .treeToggle,
.treeLine[data-fill] .treeMeta,
.treeLine[data-fill] .treeSelect[data-selected='true'] {
  color: inherit;
}

/* A filled tree as a stack of cards (decision 1420). Each card starts where its
   level's step ends, so the widths step inward with depth; a hairline of the page
   parts one card from the next; and each ancestor's rail runs down the gutter
   under its expand control, beside everything it holds. */
.tree[data-tree-fill] {
  --tree-indent: var(--tree-fill-indent);
  --tree-rail-offset: calc(var(--space-1) + (var(--icon-size-nav) - var(--tree-rail-width)) / 2);
  /* 🔒 The width of the card's strong edge; how much colour its tint carries is the token --tree-fill-tint (decision 1454). */
  --tree-fill-edge: var(--space-1);
}

/* 🔒 A filled tree's card is a soft tint of its level colour with the strong
   colour on its left edge, and the text in the ordinary ink (decision 1454,
   amending 1290 and 1420). The strong colour fills the selected card alone, in
   the ink its contrast chose. 💰 Every card was solid navy or ochre edge to edge,
   so the page was mostly blocks of colour (UI audit of 2026-09-30, rank 17). */
.tree[data-tree-fill] .treeLine[data-fill] {
  background: color-mix(in srgb, var(--tree-bar) var(--tree-fill-tint), var(--surface));
  color: var(--text);
  box-shadow: inset var(--tree-fill-edge) 0 0 var(--tree-bar);
}

.tree[data-tree-fill] .treeLine[data-fill]:not([data-selected='true']) .treeMeta {
  color: var(--text-muted);
}

.tree[data-tree-fill] .treeLine[data-fill][data-selected='true'] {
  background: var(--tree-bar);
  color: var(--tree-ink);
}

.tree[data-tree-fill] .treeRow {
  padding-bottom: var(--tree-fill-gap);
  background-image: var(--tree-rail-images, none);
  background-position: var(--tree-rail-positions, 0 0);
  background-size: var(--tree-rail-sizes, auto);
  background-repeat: no-repeat;
}

.tree[data-tree-fill] .treeBar {
  display: none;
}

.tree[data-tree-fill] .treeLine {
  flex-wrap: wrap;
  column-gap: var(--space-2);
  row-gap: 0;
  padding: var(--space-half) var(--space-2) var(--space-half) var(--space-1);
  border-bottom: 0;
  border-radius: var(--radius-small);
}

/* A group's card stands a step taller and names itself in the heavier weight; a
   leaf's card is compact and regular. */
.tree[data-tree-fill] .treeRow[data-leaf='true'] > .treeLine {
  min-height: var(--tree-fill-leaf-height);
}

.tree[data-tree-fill] .treeRow[data-leaf='false'] > .treeLine[data-fill] .treeLabel {
  font-weight: var(--font-weight-semibold);
}

/* The label keeps its own width and the facts take the right edge; where the
   card is too narrow for both, the facts wrap to a second line beneath. */
.tree[data-tree-fill] .treeLabel {
  flex: 1 1 auto;
}

.tree[data-tree-fill] .treeMeta {
  margin-left: auto;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-regular);
}

/* The selected card: a ring of the page, then a ring of the accent outside it,
   so it reads on every level colour and never repaints the fill. */
.tree[data-tree-fill] .treeLine[data-selected='true'] {
  position: relative;
  z-index: 1;
  box-shadow: 0 0 0 var(--tree-select-gap) var(--surface), 0 0 0 calc(var(--tree-select-gap) + var(--tree-select-ring)) var(--accent);
}

.tree[data-tree-fill] .treeLine[data-fill] .treeSelect[data-selected='true'] {
  font-weight: inherit;
}

/* Hover draws a hairline of the row's own ink inside the card, and focus a ring
   of it, so neither repaints the fill. */
.tree[data-tree-fill] .treeLine[data-fill]:hover:not([data-selected='true']) {
  box-shadow: inset var(--tree-fill-edge) 0 0 var(--tree-bar), inset 0 0 0 var(--control-border-width) var(--tree-bar);
}

.tree[data-tree-fill] .treeLine[data-fill] :focus-visible {
  outline: var(--focus-ring);
  outline-color: currentColor;
  outline-offset: var(--space-half);
}

.tree[data-tree-fill] .treeLine[data-fill] .treeSelect {
  font-size: inherit;
}

/* A section heading (decision 1420): plain text on the page with a rule beneath
   and room above, never a card. */
.tree[data-tree-fill] .treeRow + .treeRow[data-heading='true'] {
  margin-top: var(--space-4);
}

/* The heading's name on its first line, its facts beneath it, and the host's
   controls at the right across both, so no width squeezes the name. */
.tree[data-tree-fill] .treeRow[data-heading='true'] > .treeLine {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  min-height: var(--list-viewer-row-height);
  padding-bottom: var(--space-1);
  border-bottom: 1px solid var(--border);
  border-radius: 0;
}

.tree[data-tree-fill] .treeRow[data-heading='true'] .treeToggle {
  grid-column: 1;
  grid-row: 1;
}

.tree[data-tree-fill] .treeRow[data-heading='true'] .treeLabel {
  grid-column: 2;
  grid-row: 1;
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
}

.tree[data-tree-fill] .treeRow[data-heading='true'] .treeMeta {
  grid-column: 2;
  grid-row: 2;
  margin-left: 0;
}

.tree[data-tree-fill] .treeRow[data-heading='true'] .treeActions {
  grid-column: 3;
  grid-row: 1 / span 2;
}

/* A small square at the row's left, such as an asset's picture (decision 1420). */
.treeLead {
  flex: none;
  display: inline-flex;
  align-items: center;
}

.treeToggle {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--icon-size-nav);
  height: var(--icon-size-nav);
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.treeToggle:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.treeLabel {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--font-size-body);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.treeCount {
  flex: none;
  min-width: var(--pill-height-small);
  padding: 0 var(--space-1);
  border-radius: var(--radius-stadium);
  background: var(--surface-2);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
  text-align: center;
  line-height: var(--pill-height-small);
}

.treePreview {
  margin: var(--space-1) 0 var(--space-2) calc(var(--icon-size-nav) + var(--tree-bar-width) + var(--space-2) * 2);
  padding: var(--space-2) var(--space-3);
  border-left: var(--tree-bar-width) solid var(--surface-2);
  color: var(--text-muted);
  font-size: var(--font-size-small);
}

/* Permission Grid: a table of native checkboxes; a narrow page scrolls the grid, never the page. */
.gridWrap {
  overflow-x: auto;
}

.grid {
  border-collapse: collapse;
  font-size: var(--font-size-small);
}

.gridCaption {
  text-align: left;
  padding-bottom: var(--space-2);
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.grid th,
.grid td {
  padding: var(--space-1) var(--space-2);
  border-bottom: 1px solid var(--surface-2);
  text-align: left;
  vertical-align: middle;
}

.grid th {
  font-weight: var(--font-weight-semibold);
  color: var(--text-muted);
}

/* A heading and its whole-row or whole-column box share one line; a cell centres its box. */
.gridHead {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  white-space: nowrap;
}

.gridCell {
  text-align: center;
}

.gridCell > * {
  justify-content: center;
}

.grid th > .gridHead > label,
.gridCell > label {
  min-height: var(--button-height-medium);
}

/* Drag-and-drop list. */
.dragList {
  margin: 0;
  padding: 0;
  list-style: none;
}

.dragItem {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--list-viewer-row-height);
  padding: 0 var(--space-2);
  border: 1px solid var(--surface-2);
  border-radius: var(--radius-small);
  margin-bottom: var(--space-1);
  background: var(--surface);
  font-size: var(--font-size-body);
  cursor: grab;
}

.dragItem[data-fixed='true'] {
  cursor: default;
  color: var(--text-muted);
}

.dragItem[data-grabbed='true'] {
  border-color: var(--navy-primary);
  background: var(--selection-bg);
}

.dragItem:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.dragHandle {
  display: inline-flex;
  color: var(--text-muted);
}

.dragLabel {
  flex: 1 1 auto;
  min-width: 0;
}

.dragStatus {
  font-size: var(--font-size-caption);
  color: var(--navy-primary);
}

.dragWrap {
  position: relative;
}

/* 🔒 The keyboard instructions leave the screen and stay each item's spoken
   description; they draw only while the list holds keyboard focus (decision
   1323). A mouse click focuses an item without `:focus-visible`, so it leaves
   them hidden. */
.dragHint {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  margin: 0;
  font-size: var(--font-size-caption);
  color: var(--text-muted);
}

.dragWrap:has(:focus-visible) .dragHint {
  position: static;
  width: auto;
  height: auto;
  overflow: visible;
  clip: auto;
  white-space: normal;
  margin: var(--space-1) 0 0;
}

/* File drop zone and its queue; the whole dashed area opens the picker (decision 1328). */
.dropZone {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-height: var(--drop-zone-min-height);
  padding: var(--space-4);
  border: var(--control-border-width) dashed var(--border);
  border-radius: var(--radius-medium);
  background: var(--surface);
  cursor: pointer;
}

.dropZone[data-over='true'] {
  border-color: var(--navy-primary);
  background: var(--selection-bg);
}

.dropLabel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-muted);
  font-size: var(--font-size-small);
  cursor: pointer;
}

/* 🔒 The browser's own control is hidden and kept (decision 1328): still in the page, still focusable, still named by the label. */
.dropInput {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  opacity: 0;
}

/* The label's face: the outlined secondary button, the Inactive Button's own look at Medium. */
.dropChoose {
  display: inline-flex;
  align-items: center;
  height: var(--button-height-medium);
  padding: 0 calc(var(--button-height-medium) * 0.5);
  border-radius: calc(var(--button-height-medium) * var(--button-radius-ratio));
  background: var(--surface);
  box-shadow: inset 0 0 0 var(--field-border-width) var(--status-inactive-text);
  color: var(--status-inactive-text);
  font-size: var(--font-size-button-medium);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
}

.dropChoose:hover {
  background: var(--surface-2);
}

/* The keyboard's focus sits on the hidden input, so the ring is drawn on the button that stands for it. */
.dropInput:focus-visible + .dropLabel .dropChoose {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* The one-file face names the file it holds (decision 1450). */
.dropPicked {
  margin: 0;
  font-size: var(--font-size-small);
  color: var(--text);
}

.dropQueue {
  margin: 0;
  padding: 0;
  list-style: none;
}

.dropRow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-1) var(--space-2);
  align-items: center;
  padding: var(--space-2) 0;
  border-top: 1px solid var(--surface-2);
  font-size: var(--font-size-small);
}

.dropName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropMeta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

/* 🔒 Decision 11301: a queued file's own fields, its name and its date, on a line of their own under its name. */
.dropFields {
  display: flex;
  grid-column: 1 / -1;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--space-2);
}

.dropProgress {
  grid-column: 1 / -1;
  width: 100%;
  height: var(--space-2);
  accent-color: var(--navy-primary);
}

.dropRow[data-upload='failed'] .dropProgress {
  accent-color: var(--status-error-text);
}

.dropStatus {
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.dropRow[data-upload='failed'] .dropStatus {
  color: var(--status-error-text);
}

.iconButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--button-height-small);
  height: var(--button-height-small);
  padding: 0;
  border: 0;
  border-radius: var(--radius-small);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
}

.iconButton:hover:not(:disabled) {
  background: var(--surface-2);
  color: var(--text);
}

.iconButton:disabled {
  opacity: var(--control-disabled-opacity);
  cursor: not-allowed;
}

.iconButton:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

/* File Viewer. */
.viewer {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--surface-2);
  border-radius: var(--radius-medium);
  overflow: hidden;
}

.viewerBar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  min-height: var(--tile-header-height);
  padding: 0 var(--space-3);
  background: var(--surface-2);
}

.viewerActions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.viewerBody {
  position: relative;
  min-height: calc(var(--space-6) * 5);
  padding: var(--space-4);
  overflow: hidden;
}

/* The same permission that removed the Download control keeps a View-only
   document off paper when print is reached some other way. */
@media print {
  .viewer[data-permission='View'] .viewerBody {
    display: none;
  }
}

/* Address Verification. */
.address {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.addressHead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.addressRow {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

@media (--bp-md) {
  .addressRow {
    flex-direction: row;
  }

  /* Each field stands at its own width and the row packs to the left, so no gap opens beside a capped box (decision 1328). */
  .addressRow > * {
    flex: 0 1 var(--field-width-medium);
    min-width: 0;
  }
}

/* The State field keeps its small width beside City and ZIP Code (decision 1328). */
@media (--bp-md) {
  .addressRow > .addressState {
    flex-basis: var(--field-width-small);
  }
}

/* + Add Line 2: a small link-shaped button where the empty second line stood (decision 1328). */
.addressAddLine {
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--navy-primary);
  font: inherit;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}

.addressAddLine:hover {
  text-decoration: underline;
}

.addressAddLine:focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.suggestion {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border-left: var(--tree-bar-width) solid var(--status-warning-text);
  border-radius: var(--radius-small);
  background: var(--status-warning-bg);
}

/* 🔒 The suggested address, drawn line by line as the record's own address is (T563). */
.suggestionAddress {
  display: flex;
  flex-direction: column;
  font-style: normal;
}

.suggestionLine {
  display: block;
}

.suggestionActions {
  display: flex;
  gap: var(--space-2);
}

.addressFailed {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  color: var(--status-error-text);
  font-size: var(--font-size-small);
}

/* A selectable tree row (piece 16's category tree): the label is the control and the selected row reads bold on the surface tint. */
.treeSelect {
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.treeSelect[data-selected='true'] {
  font-weight: var(--font-weight-bold);
  color: var(--navy-primary);
}

/* The org chart's rows (piece 10, decision 0079): the facts beside the name, the
   row's own controls at its right, and the row a drag is currently over. */
.treeMeta {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-muted);
  font-size: var(--font-size-caption);
}

.treeActions {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

.treeLine[data-drop-target='true'] {
  background: var(--surface-2);
  outline: var(--focus-ring);
  outline-offset: calc(var(--focus-ring-offset) * -1);
}

/* An item's own controls in place of its label (row T2840): the body takes the line's width. */
.dragBody {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
}
